import React, { forwardRef } from 'react';
import { LucideIcon } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  leftIcon?: LucideIcon;
  label?: string;
  helperText?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, leftIcon: LeftIcon, label, helperText, className = '', id, ...props }, ref) => {
    const inputId = id || props.name;
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {LeftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <LeftIcon className={`w-5 h-5 ${hasError ? 'text-red-400' : 'text-gray-400'}`} />
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={`
              w-full bg-gray-800 border rounded-lg py-2 px-4 text-white
              placeholder-gray-500
              focus:outline-none focus:ring-2 focus:border-transparent
              transition-colors
              ${LeftIcon ? 'pl-10' : ''}
              ${hasError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-700 focus:ring-blue-500'
              }
              ${className}
            `}
            {...props}
          />
        </div>

        {(error || helperText) && (
          <p className={`mt-1 text-sm ${hasError ? 'text-red-400' : 'text-gray-500'}`}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;

// Textarea variant
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  label?: string;
  helperText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error, label, helperText, className = '', id, ...props }, ref) => {
    const textareaId = id || props.name;
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-300 mb-2"
          >
            {label}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          className={`
            w-full bg-gray-800 border rounded-lg py-2 px-4 text-white
            placeholder-gray-500 resize-none
            focus:outline-none focus:ring-2 focus:border-transparent
            transition-colors
            ${hasError
              ? 'border-red-500 focus:ring-red-500'
              : 'border-gray-700 focus:ring-blue-500'
            }
            ${className}
          `}
          {...props}
        />

        {(error || helperText) && (
          <p className={`mt-1 text-sm ${hasError ? 'text-red-400' : 'text-gray-500'}`}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
